'use client'

import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'sonner'
import { RootState } from '@/stores/redux/store'
import { hideAlert } from '@/stores/redux/features/alerts/slice'
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react'
import { useMonochrome } from '@/providers/MonochromeProvider'

const Alert = () => {
  const dispatch = useDispatch()
  const { message, severity, displayAlert } = useSelector(
    (state: RootState) => state.alert
  )
  const { isMonochrome } = useMonochrome()

  useEffect(() => {
    if (displayAlert && message) {
      const toastId = `toast-${Date.now()}`

      // Monochrome icons - all use foreground color
      const monochromeIconMap = {
        success: <CheckCircle className="h-5 w-5 text-foreground" />,
        error: <XCircle className="h-5 w-5 text-foreground" />,
        warning: <AlertTriangle className="h-5 w-5 text-foreground" />,
        info: <Info className="h-5 w-5 text-foreground" />,
      }

      // Colored icons
      const coloredIconMap = {
        success: <CheckCircle className="h-5 w-5 text-green-500" />,
        error: <XCircle className="h-5 w-5 text-red-500" />,
        warning: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
        info: <Info className="h-5 w-5 text-blue-500" />,
      }

      const iconMap = isMonochrome ? monochromeIconMap : coloredIconMap

      // Monochrome background - neutral gray
      const monochromeBg = 'bg-card border-border'

      // Colored backgrounds
      const coloredBgMap = {
        success: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800',
        error: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800',
        warning: 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800',
        info: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800',
      }

      const bgClass = isMonochrome ? monochromeBg : coloredBgMap[severity]

      toast.custom(
        (t) => (
          <div
            onClick={() => toast.dismiss(t)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg cursor-pointer transition-opacity hover:opacity-80 ${bgClass}`}
          >
            {iconMap[severity]}
            <span className="text-sm font-medium text-foreground">{message}</span>
          </div>
        ),
        { id: toastId, duration: 4000 }
      )

      // Hide the alert after showing toast
      dispatch(hideAlert())
    }
  }, [displayAlert, message, severity, dispatch, isMonochrome])

  // The actual toast rendering is handled by the Toaster component in RootLayoutClient
  return null
}

export default Alert
